import { Box, BoxProps, Text } from '@chakra-ui/react';
import { AutoFitText } from './AutoFitText';

type Align = 'left' | 'center' | 'right';

type ApplePassFieldOwnProps = {
  label?: string;
  id?: string;
  value?: string;
  align?: Align;
  titleColor?: string;
  bodyColor?: string;
  isHighlighted?: boolean;
  highlightColor?: string;
  minFontSize?: number;
  maxFontSize?: number;
  columnHeight?: BoxProps['height'];
  valueHeight?: BoxProps['height'];
  fontSize?: string;
  defaultText?: string;
  hideIdPlaceholder?: boolean;
};

export type ApplePassFieldProps = ApplePassFieldOwnProps & BoxProps;

export const ApplePassField: React.FC<ApplePassFieldProps> = ({
  label,
  id,
  value,
  align = 'left',
  titleColor,
  bodyColor,
  isHighlighted = false,
  highlightColor = '#FF4935',
  minFontSize = 8,
  maxFontSize = 14,
  columnHeight = '30px',
  valueHeight = '16px',
  fontSize,
  defaultText,
  hideIdPlaceholder = false,
  ...boxProps
}) => {
  const showIdPlaceholder = !hideIdPlaceholder && id && !value;

  if (!label) {
    const textValue = value || defaultText || '';
    if (!textValue && !showIdPlaceholder && !isHighlighted && !id) return null;

    return (
      <Box
        position="relative"
        p="4px"
        minH={isHighlighted && !textValue && !showIdPlaceholder ? '24px' : undefined}
        {...boxProps}
      >
        {isHighlighted && (
          <Box
            position="absolute"
            inset={0}
            border={`2px solid ${highlightColor}`}
            borderRadius="4px"
            pointerEvents="none"
          />
        )}
        {(textValue || showIdPlaceholder) && (
          <Text
            fontSize={fontSize || '15px'}
            textStyle="headingFamilyRegularApplePay"
            textAlign={align}
            wordBreak="break-word"
            color={bodyColor}
          >
            {showIdPlaceholder ? `<${id}>` : textValue}
          </Text>
        )}
      </Box>
    );
  }

  if (!id && !value && !isHighlighted) return null;

  const textAlign = align;

  const resolvedValue = showIdPlaceholder ? `<${id}>` : value || '';

  return (
    <Box
      position="relative"
      overflow="hidden"
      p="4px"
      {...boxProps}
    >
      {isHighlighted && (
        <Box
          position="absolute"
          inset={0}
          border={`2px solid ${highlightColor}`}
          borderRadius="4px"
          pointerEvents="none"
          zIndex={1}
        />
      )}
      <Box
        minWidth="40px"
        textAlign={textAlign}
        display="flex"
        flexDir="column"
        height={columnHeight}
      >
        {/* Label */}
        <Text
          fontSize="10px"
          lineHeight="1"
          textStyle="headingFamilyMediumApplePay"
          color={titleColor}
          textTransform="uppercase"
          isTruncated
          minH="14px"
        >
          {label || ''}
        </Text>

        {/* Value */}
        <AutoFitText
          minFontSize={minFontSize}
          maxFontSize={maxFontSize}
          containerHeight={
            typeof valueHeight === 'string'
              ? valueHeight
              : valueHeight.toString()
          }
          verticalAlign="bottom"
          textStyle="headingFamilyRegularApplePay"
          color={bodyColor}
          textAlign={textAlign}
          isTruncated
        >
          {resolvedValue}
        </AutoFitText>
      </Box>
    </Box>
  );
};
